/**
 * projectContainerSectionsIntakeEventToState
 *
 * Applies realtime intake events (added/removed/moved/updated) to the
 * ProjectContainerSectionModel[] home-feed state for project containers.
 *
 * This keeps the per-section folders + intakes list in sync with
 * ProjectContainerEvent updates from SignalR without requiring refetches.
 */
import type {
  ProjectContainerSectionModel,
  ProjectContainerOverviewFolder,
  ProjectContainerOverviewIntake
} from 'src/features/companyProjects/projectContainers/types/ProjectContainerHomeTypes';
import { normalizeHomeFeedIntake } from 'src/features/companyProjects/projectContainers/dashboard/api/normalizeHomeFeedSections';
import type { ProjectContainerEvent } from '../events/projectContainerEventTypes';
import { ProjectSectionIntakeEventType } from '../events/projectContainerEventTypes';

const numericCount = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const folderIntakeCount = (folder: ProjectContainerOverviewFolder) =>
  numericCount(folder.IntakeCount ?? (folder as { intakeCount?: number }).intakeCount, folder.Intakes?.length ?? 0);

const folderUnreadCount = (folder: ProjectContainerOverviewFolder) =>
  numericCount(folder.UnreadCount ?? (folder as { unreadCount?: number }).unreadCount, 0);

const withCountDelta = (
  folder: ProjectContainerOverviewFolder,
  intakeDelta: number,
  unreadDelta: number
): ProjectContainerOverviewFolder => ({
  ...folder,
  IntakeCount: Math.max(0, folderIntakeCount(folder) + intakeDelta),
  UnreadCount: Math.max(0, folderUnreadCount(folder) + unreadDelta)
});

const isUnreadIntake = (intake: ProjectContainerOverviewIntake | undefined) => intake?.HasNewMessage === true;

// ----- Sections/home-feed helpers -----

const cloneSections = (
  state: ProjectContainerSectionModel[] | undefined
): ProjectContainerSectionModel[] | undefined => {
  if (!state) return state;
  return state.map((section) => ({
    ...section,
    Folders: section.Folders
      ? section.Folders.map<ProjectContainerOverviewFolder>((folder) => ({
          ...folder,
          Intakes: folder.Intakes ? [...folder.Intakes] : undefined
        }))
      : section.Folders
  }));
};

// ----- Sections/home-feed reducer (intake events) -----

export const applyContainerEventToSectionsState = (
  state: ProjectContainerSectionModel[] | undefined,
  event: ProjectContainerEvent
): ProjectContainerSectionModel[] | undefined => {
  const current = state;
  if (!current || current.length === 0) {
    return state;
  }

  const { intakeId, fromFolderId, toFolderId, data, type } = event;

  switch (type) {
    case ProjectSectionIntakeEventType.IntakeAdded: {
      if (!toFolderId || !data) return state;
      const next = cloneSections(current);
      if (!next) return state;

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== toFolderId) return folder;
          const existing = folder.Intakes ?? [];
          const alreadyListed = existing.some((x) => x.Id === intakeId);
          const newItem = normalizeHomeFeedIntake({ ...(data as object as Record<string, unknown>), Id: intakeId });
          sectionChanged = true;
          const next = withCountDelta(
            folder,
            alreadyListed ? 0 : 1,
            alreadyListed ? 0 : isUnreadIntake(newItem) ? 1 : 0
          );
          if (existing.length === 0) {
            return next;
          }
          return {
            ...next,
            Intakes: [newItem, ...existing.filter((x) => x.Id !== intakeId)]
          };
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    case ProjectSectionIntakeEventType.IntakeRemoved: {
      if (!fromFolderId) return state;
      const next = cloneSections(current);
      if (!next) return state;

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== fromFolderId) return folder;
          const existing = folder.Intakes ?? [];
          const removed = existing.find((x) => x.Id === intakeId);
          sectionChanged = true;
          const next = withCountDelta(folder, -1, isUnreadIntake(removed) ? -1 : 0);
          if (existing.length === 0) {
            return next;
          }
          return {
            ...next,
            Intakes: existing.filter((x) => x.Id !== intakeId)
          };
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    case ProjectSectionIntakeEventType.IntakeMoved: {
      // toFolderId is always required. fromFolderId is absent when assigning from
      // the global inbox into a container — backend sends FromFolderId null; domain
      // still runs MarkAsUnread after MoveIntake so `data` carries HasNewMessage.
      if (!toFolderId) return state;

      // Find the existing intake in the original state so we can
      // preserve its Subject (and any other fields we track locally)
      // when moving between folders. Some move events do not include
      // a full `data` payload, which previously caused the Subject to
      // be dropped in other windows.
      let movedIntake: ProjectContainerOverviewIntake | undefined = undefined;

      if (fromFolderId != null) {
        for (const section of current) {
          if (!section.Folders) continue;
          for (const folder of section.Folders) {
            if (folder.Id !== fromFolderId || !folder.Intakes) continue;
            const found = folder.Intakes.find((x) => x.Id === intakeId);
            if (found) {
              movedIntake = found;
              break;
            }
          }
          if (movedIntake) break;
        }
      }

      const next = cloneSections(current);
      if (!next) return state;

      const buildNewItem = (): ProjectContainerOverviewIntake => {
        const merged =
          movedIntake != null
            ? { ...movedIntake, ...(data as object as Record<string, unknown>) }
            : { ...(data as object as Record<string, unknown>), Id: intakeId };
        return normalizeHomeFeedIntake(merged);
      };

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (fromFolderId != null && folder.Id === fromFolderId) {
            const existing = folder.Intakes ?? [];
            const removed = existing.find((x) => x.Id === intakeId);
            const leaving =
              removed ?? (data ? normalizeHomeFeedIntake({ ...(data as object), Id: intakeId }) : undefined);
            sectionChanged = true;
            const next = withCountDelta(folder, -1, isUnreadIntake(leaving) ? -1 : 0);
            if (existing.length === 0) {
              return next;
            }
            return {
              ...next,
              Intakes: existing.filter((x) => x.Id !== intakeId)
            };
          }

          if (folder.Id === toFolderId) {
            const existing = folder.Intakes ?? [];
            const alreadyListed = existing.some((x) => x.Id === intakeId);
            const newItem = buildNewItem();
            sectionChanged = true;
            const next = withCountDelta(
              folder,
              alreadyListed ? 0 : 1,
              alreadyListed ? 0 : isUnreadIntake(newItem) ? 1 : 0
            );
            if (existing.length === 0) {
              return next;
            }
            return {
              ...next,
              Intakes: [newItem, ...existing.filter((x) => x.Id !== intakeId)]
            };
          }

          return folder;
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    case ProjectSectionIntakeEventType.IntakeUpdated: {
      if (!toFolderId || !data) return state;
      const next = cloneSections(current);
      if (!next) return state;

      let changed = false;
      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== toFolderId || !folder.Intakes || folder.Intakes.length === 0) return folder;
          const current = folder.Intakes.find((item) => item.Id === intakeId);
          if (!current) return folder;
          const updated = normalizeHomeFeedIntake({ ...current, ...(data as object as Record<string, unknown>) });
          const unreadDelta = (isUnreadIntake(updated) ? 1 : 0) - (isUnreadIntake(current) ? 1 : 0);
          sectionChanged = true;
          return {
            ...withCountDelta(folder, 0, unreadDelta),
            Intakes: folder.Intakes.map((item) => (item.Id === intakeId ? updated : item))
          };
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    default:
      return state;
  }
};
